import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const REJECT_TAGS = [
    { value: 'no_es_persona', label: 'No es la persona' },
    { value: 'no_es_domicilio', label: 'No es el domicilio' },
    { value: 'documento_no_legible', label: 'Documento no legible' },
];

const RejectModal = ({ isOpen, onClose, item, onConfirmReject }) => {
    const [selectedTags, setSelectedTags] = useState([]);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    const toggleTag = (tag) => {
        setSelectedTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        );
    };

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await onConfirmReject(item, {
                tags: selectedTags.length > 0 ? selectedTags : undefined,
                reason: reason.trim() || undefined,
            });
            setSelectedTags([]);
            setReason('');
            onClose();
        } catch (err) {
            console.error('[RejectModal] reject failed:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedTags([]);
        setReason('');
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && item && (
                <div
                    onClick={handleClose}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1100,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', padding: '1rem'
                    }}
                >
                    <motion.div
                        onClick={(e) => e.stopPropagation()}
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        style={{
                            backgroundColor: '#ffffff', borderRadius: '1rem',
                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                            width: '100%', maxWidth: '28rem', overflow: 'hidden'
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            padding: '1.25rem 1.5rem', borderBottom: '1px solid #fecaca',
                            backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', gap: '0.75rem'
                        }}>
                            <i className="pi pi-times-circle" style={{ fontSize: '1.5rem', color: '#ef4444' }}></i>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold', color: '#991b1b' }}>Rechazar documento</h3>
                                <p style={{ margin: 0, fontSize: '0.8rem', color: '#b91c1c' }}>
                                    {item.category} — {item.fileName}
                                </p>
                            </div>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '0.5rem', display: 'block' }}>
                                    Motivo rápido (opcional)
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    {REJECT_TAGS.map(tag => {
                                        const isSelected = selectedTags.includes(tag.value);
                                        return (
                                            <button
                                                key={tag.value}
                                                onClick={() => toggleTag(tag.value)}
                                                style={{
                                                    padding: '0.375rem 0.75rem', borderRadius: '9999px',
                                                    fontSize: '0.8rem', fontWeight: '500', cursor: 'pointer',
                                                    transition: 'all 0.2s', border: '1px solid',
                                                    backgroundColor: isSelected ? '#fef2f2' : '#f8fafc',
                                                    color: isSelected ? '#dc2626' : '#64748b',
                                                    borderColor: isSelected ? '#fca5a5' : '#e2e8f0',
                                                }}
                                            >
                                                {isSelected && <i className="pi pi-check" style={{ fontSize: '0.7rem', marginRight: '0.25rem' }}></i>}
                                                {tag.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '0.5rem', display: 'block' }}>
                                    Comentario adicional (opcional)
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Describe por qué rechazas este documento..."
                                    maxLength={500}
                                    rows={3}
                                    style={{
                                        width: '100%', padding: '0.625rem', borderRadius: '0.5rem',
                                        border: '1px solid #e2e8f0', fontSize: '0.875rem', resize: 'vertical',
                                        fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0',
                            display: 'flex', justifyContent: 'flex-end', gap: '0.75rem'
                        }}>
                            <button
                                onClick={handleClose}
                                disabled={loading}
                                style={{
                                    padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0',
                                    backgroundColor: '#ffffff', color: '#475569', fontWeight: '500',
                                    fontSize: '0.875rem', cursor: 'pointer'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={loading}
                                style={{
                                    padding: '0.5rem 1rem', borderRadius: '0.5rem', border: 'none',
                                    backgroundColor: '#ef4444', color: '#ffffff', fontWeight: '500',
                                    fontSize: '0.875rem', cursor: loading ? 'wait' : 'pointer',
                                    opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem',
                                }}
                            >
                                {loading ? (
                                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }}></i>
                                ) : (
                                    <i className="pi pi-ban" style={{ fontSize: '1rem' }}></i>
                                )}
                                Confirmar Rechazo
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default RejectModal;
